"use client";

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUserFriends, FaLayerGroup, FaReceipt,
  FaGamepad, FaHandshake, FaChartLine,
  FaCheckCircle, FaArrowRight
} from 'react-icons/fa';
import './FeaturesSection.css';

const FEATURES = [
  {
    icon: <FaUserFriends />,
    title: "F2F Lending",
    description: "Secure peer-to-peer lending with digital agreements, automated reminders, and transaction tracking.",
    details: [
      "Digital IOU notes with e-signatures",
      "Smart repayment reminders",
      "Transaction history & analytics",
      "Secure escrow for large amounts"
    ],
    color: "#6C63FF",
    useCases: ["Personal Loans", "Emergency Funds", "Business Capital", "Education Fees"],
    image: "/Assets/f2f.png"
  },
  {
    icon: <FaLayerGroup />,
    title: "Group Funds",
    description: "Create collective funds for travel, parties, college fests, or community projects.",
    details: [
      "Weekly/Monthly automated contributions",
      "Goal tracking & progress visualization",
      "Multiple withdrawal authorizations",
      "Transparent fund utilization reports"
    ],
    color: "#FF6584",
    useCases: ["Travel Plans", "Wedding Funds", "Fest Collections", "NGO Donations"],
    image: "/Assets/group-fund.png"
  },
  {
    icon: <FaReceipt />,
    title: "Smart Bill Splitting",
    description: "Split bills intelligently with automated settlements and payment tracking.",
    details: [
      "OCR-based bill scanning",
      "Multiple split patterns (equal, percentage, custom)",
      "Auto-reminders for pending payments",
      "Batch settlement options"
    ],
    color: "#00D4AA",
    useCases: ["Restaurant Bills", "Groceries", "Travel Expenses", "Household Bills"],
    image: "/assets/bill-split.png"
  },
  {
    icon: <FaGamepad />,
    title: "Gamified Finance",
    description: "Educational finance games with real rewards from partner brands.",
    details: [
      "Family finance challenges",
      "Investment simulation games",
      "Reward-based learning modules",
      "Partner coupons & vouchers"
    ],
    color: "#F5C842",
    useCases: ["Learning Games", "Family Challenges", "Reward Programs", "Skill Building"],
    image: "/assets/gamified-finance.png"
  },
  {
    icon: <FaHandshake />,
    title: "Community Pods",
    description: "Create mini-banking communities with democratic governance and shared funds.",
    details: [
      "Democratic interest rate decisions",
      "Multi-signature withdrawals",
      "Customizable lending rules",
      "Real-time fund growth tracking"
    ],
    color: "#9D4EDD",
    useCases: ["College Groups", "Society Funds", "Office Teams", "Village Committees"],
    image: "/Assets/community-pods.png"
  },
  {
    icon: <FaChartLine />,
    title: "Social Credit Graph",
    description: "Alternative credit scoring based on real social transactions and reputation.",
    details: [
      "Network-weighted scoring algorithm",
      "Transaction history analysis",
      "Peer endorsements system",
      "Creditworthiness visualization"
    ],
    color: "#2ECC71",
    useCases: ["Loan Eligibility", "Network Trust", "Reputation Building", "Extended Networks"],
    image: "/Assets/social-credit.png"
  }
];

const FeaturesSection = ({ featuresRef, featuresInView }) => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimer = useRef(null);

  const handleTabClick = useCallback((index) => {
    setActiveFeature(index);
    setPaused(true);
    clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), 8000);
  }, []);

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % FEATURES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [paused]);

  useEffect(() => () => clearTimeout(resumeTimer.current), []);

  const active = FEATURES[activeFeature];

  return (
    <section className="features-section" id="features" ref={featuresRef}>
      <div className="features-container">

        {/* ── Section header ── */}
        <div className="section-header">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Complete Social Finance Platform
          </motion.h2>
          <motion.p
            className="section-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Everything you need to manage money with friends, family,
            and communities in one secure platform
          </motion.p>
        </div>

        {/* ── Feature tabs ── */}
        <div className="feature-tabs-wrapper">
          <div className="feature-tabs" role="tablist" aria-label="Features">
            {FEATURES.map((feature, index) => (
              <motion.button
                key={feature.title}
                className={`feature-tab ${activeFeature === index ? 'active' : ''}`}
                onClick={() => handleTabClick(index)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.07 }}
                whileTap={{ scale: 0.96 }}
                style={{ '--tab-color': feature.color }}
                role="tab"
                aria-selected={activeFeature === index}
                aria-label={feature.title}
              >
                <div
                  className="tab-icon"
                  style={{ color: activeFeature === index ? feature.color : undefined }}
                >
                  {feature.icon}
                </div>
                <span>{feature.title}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* ── Active feature display ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature}
            className="feature-content"
            style={{ '--tab-color': active.color }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.28 }}
            role="tabpanel"
            aria-label={active.title}
          >
            {/* ── Left: details ── */}
            <div className="feature-details">
              <div className="feature-header">
                <div className="feature-header-text">
                  {/* Title — no inline font-family so CSS takes over */}
                  <h3 style={{ color: active.color }}>
                    {active.title}
                  </h3>
                  <p>{active.description}</p>
                </div>

                {/* Mobile icon */}
                <div
                  className="feature-header-icon"
                  style={{
                    backgroundColor: active.color + '18',
                    borderColor:      active.color + '35',
                  }}
                >
                  <img
                    src={active.image}
                    alt={active.title}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    loading="lazy"
                  />
                </div>
              </div>

              <ul className="feature-list">
                {active.details.map((detail, idx) => (
                  <motion.li
                    key={detail}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.07 }}
                  >
                    <FaCheckCircle
                      className="check-icon"
                      style={{ color: active.color }}
                    />
                    {detail}
                  </motion.li>
                ))}
              </ul>

              <div className="feature-use-cases">
                {active.useCases.map((useCase) => (
                  <span
                    key={useCase}
                    className="use-case-tag"
                    style={{
                      backgroundColor: active.color + '18',
                      color:            active.color,
                      borderColor:      active.color + '35',
                    }}
                  >
                    {useCase}
                  </span>
                ))}
              </div>

              <motion.button
                className="learn-more-btn"
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.96 }}
                style={{ backgroundColor: active.color }}
                aria-label={`Learn more about ${active.title}`}
              >
                Learn More <FaArrowRight />
              </motion.button>
            </div>

            {/* ── Right: visual (desktop only) ── */}
            <div className="feature-visual">
              <div
                className="feature-image"
                style={{ backgroundColor: active.color + '10' }}
              >
                <img
                  src={active.image}
                  alt={active.title}
                  className="feature-main-image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};

export default FeaturesSection;
